import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { ReplaySubject } from "rxjs";
import { isPlatformBrowser } from "@angular/common";

import { IAppConfiguration, IAppConfigurationConditions } from "../models/configuration";
import { IAppConfigurationOptions } from "../models/app-configuration-options";
import { IRole } from "../models/role";
import { StorageManagerService } from "@app/core/services/storage-manager.service";
import { HttpCemex, CmxHttpOptions } from "@cemex-core/angular-services-v8";
import { HttpHeaders } from "@angular/common/http";

@Injectable()
export class AppConfigurationService {
  private static _cachedConfigurations: IAppConfiguration[] = [];
  private get _country(): string | undefined {
    return this._getCountry();
  }
  private get _userRoles(): IRole[] {
    return this._getAllUserRoles();
  }
  public configurationsLoaded: ReplaySubject<boolean>;
  public get configurations(): IAppConfiguration[] {
    return AppConfigurationService._cachedConfigurations;
  }

  private memoizedIsEnabled: Map<string, boolean> = new Map();
  private memoizedGetConfiguration: Map<string, IAppConfiguration | undefined> = new Map();
  private window: any;

  constructor(
    private http: HttpCemex,
    private storageManagerService: StorageManagerService,
    // @Inject("WINDOW_TOKEN") private window: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject("AppConfigurationOptions") public readonly configuration: IAppConfigurationOptions
  ) {
    if (isPlatformBrowser(platformId)) {
      this.window = window;

      if (["Quality", "Development"].some((env) => env == configuration.environment)) {
        Object.defineProperty(window, "DEBUG__APPCONFIGURATIONS__LIST", {
          value: this.configurations,
          writable: false,
          enumerable: false,
          configurable: false,
        });

        Object.defineProperty(window, "DEBUG__APPCONFIGURATIONS__ENV", {
          value: this.configuration,
          writable: false,
          enumerable: false,
          configurable: false,
        });
      }
    }

    this.configurationsLoaded = new ReplaySubject<boolean>();

    this._loadConfigurations();
  }

  public isEnabled(configuration: string, version?: string, release?: string): boolean {
    const ConfigurationName: string = this._getConfigurationName(configuration);
    const ConfigurationVersion: string | undefined = version || this._getConfigurationVersion(configuration);
    const releaseVersion: string | undefined = release || this._getProductReleaseVersion(configuration);
    const targetConfiguration: IAppConfiguration | undefined = this._getTargetConfiguration(
      ConfigurationName,
      ConfigurationVersion,
      releaseVersion
    );

    const memoKey: string = this.serializeMemoKey(targetConfiguration, this._country, this._userRoles);
    // tslint:disable-next-line: triple-equals
    const isMemoized: boolean = this.memoizedIsEnabled.get(memoKey) != undefined;
    if (isMemoized) {
      return this.memoizedIsEnabled.get(memoKey) ? true : false;
    }

    if (!targetConfiguration) {
      if (!isMemoized) {
        this.memoizedIsEnabled.set(memoKey, false);
      }
      return false;
    }

    if (targetConfiguration.includes) {
      if (
        targetConfiguration.country ||
        targetConfiguration.role ||
        targetConfiguration.role_match ||
        targetConfiguration.role_any
      ) {
        console.warn(
          `There's a %cincludes%c property and also root properties defined.
                              Property %cincludes%c will take precedence over the feature root properties`,
          "color: blue",
          "",
          "color: blue",
          "",
          targetConfiguration
        );
      }
      targetConfiguration.country = targetConfiguration.includes.country;
      targetConfiguration.role = targetConfiguration.includes.role;
      targetConfiguration.role_match = targetConfiguration.includes.role_match;
      targetConfiguration.role_any = targetConfiguration.includes.role_any;
    }

    let neededRoles: string[] = [];
    try {
      /* this boolean operation is ordered by precedence.
            A defined Role will take over a Role_Match defined and these both will take precedence over Role_Any */
      neededRoles = this._splitString(
        targetConfiguration.role || targetConfiguration.role_match || targetConfiguration.role_any
      );
    } catch (e) {
      console.warn(
        `There's no role specified.
                         Should there be a %crole%c, %crole_match%c or %crole_any%c property in your feature declaration?`,
        "color: blue",
        "",
        "color: blue",
        "",
        "color: blue",
        "",
        targetConfiguration
      );
    }

    if (targetConfiguration.excludes) {
      // TODO: check country
      if (
        !this._matchExcludeConfigurationConditions(
          targetConfiguration.excludes,
          this._country || "",
          this._userRoles,
          neededRoles
        )
      ) {
        if (!isMemoized) {
          this.memoizedIsEnabled.set(memoKey, false);
        }
        return false;
      }
    }

    let hasMatchedRole = false;
    let hasMatchedCountry = false;
    let hasMatchedEnvironment = false;
    let hasMatchedHostname = false;
    let hasMatchedConfigurationVersion = false;
    let hasMatchedReleaseVersion = false;

    if (Boolean(targetConfiguration.role_match)) {
      hasMatchedRole = this._roleMatch(
        this._userRoles.map((x: IRole) => x.roleCode),
        neededRoles
      );
    } else {
      hasMatchedRole = this._roleAny(
        this._userRoles.map((x: IRole) => x.roleCode),
        neededRoles
      );
    }

    // make a role always match if none is defined
    hasMatchedRole =
      (!targetConfiguration.role && !targetConfiguration.role_match && !targetConfiguration.role_any) || hasMatchedRole;

    // make a country always match if there's none defined in the feature
    // tslint:disable-next-line:comment-format
    // TODO: Check country
    hasMatchedCountry =
      !targetConfiguration.country ||
      this._matchAny([this._country || ""], this._splitString(targetConfiguration.country));

    // make environment always match if none is defined in the feature
    hasMatchedEnvironment =
      !targetConfiguration.environment ||
      this._matchAny([this.configuration.environment], this._splitString(targetConfiguration.environment));
    // hasMatchedEnvironment = !targetFeature.environment || ( this.configuration.environment === targetFeature.environment );

    // make hostname always match if none is defined in the feature
    hasMatchedHostname =
      !targetConfiguration.host_name || this.configuration.hostname === targetConfiguration.host_name;

    // make a feature always match if there's no specified version
    hasMatchedConfigurationVersion =
      !ConfigurationVersion ||
      !targetConfiguration.feature_version ||
      ConfigurationVersion === targetConfiguration.feature_version;

    // make a feature always match if there's no specified releaseVersion
    hasMatchedReleaseVersion =
      !releaseVersion || !targetConfiguration.release_version || releaseVersion === targetConfiguration.release_version;

    const isEnabled: boolean =
      hasMatchedRole &&
      hasMatchedCountry &&
      hasMatchedEnvironment &&
      hasMatchedHostname &&
      hasMatchedConfigurationVersion &&
      hasMatchedReleaseVersion;

    if (!isMemoized) {
      this.memoizedIsEnabled.set(memoKey, isEnabled);
    }

    return isEnabled;
  }

  public getConfiguration(configuration: string, version?: string, release?: string): IAppConfiguration | undefined {
    const configurationName: string = this._getConfigurationName(configuration);
    const configurationVersion: string | undefined = version || this._getConfigurationVersion(configuration);
    const releaseVersion: string | undefined = release || this._getProductReleaseVersion(configuration);
    return this._getTargetConfiguration(configurationName, configurationVersion, releaseVersion);
  }

  private _matchExcludeConfigurationConditions(
    ConfigurationConditions: IAppConfigurationConditions,
    country: string,
    userRoles: IRole[],
    neededRoles: string[]
  ): boolean {
    let hasMatchedRole = false;
    let hasMatchedCountry = false;

    if (Boolean(ConfigurationConditions.role_match)) {
      hasMatchedRole = this._roleMatch(
        userRoles.map((x: IRole) => x.roleCode),
        neededRoles
      );
    } else {
      hasMatchedRole = this._roleAny(
        userRoles.map((x: IRole) => x.roleCode),
        neededRoles
      );
    }

    hasMatchedCountry = this._matchAny([country], this._splitString(ConfigurationConditions.country));

    return !(hasMatchedRole || hasMatchedCountry);
  }

  private _getTargetConfiguration(
    configuration: string,
    version: string | undefined,
    release: string | undefined
  ): IAppConfiguration | undefined {
    const memoKey: string = this.serializeMemoKey(configuration, version, release, this._country, this._userRoles);
    const isMemoized: boolean = this.memoizedGetConfiguration.get(memoKey) != undefined;
    if (isMemoized) {
      return this.memoizedGetConfiguration.get(memoKey);
    }

    let targetConfigurations: IAppConfiguration[];
    let targetConfiguration: IAppConfiguration | undefined;

    targetConfigurations = AppConfigurationService._cachedConfigurations.filter(
      (x: IAppConfiguration) =>
        this._getConfigurationName(x.feature) === configuration &&
        version === x.feature_version &&
        release === x.release_version
    );

    if (targetConfigurations.length === 0) {
      console.error(
        `The feature %c${configuration}%c with the expected criteria does not exist`,
        "color: blue",
        "",
        configuration,
        this._country,
        this._userRoles
      );
      if (!isMemoized) {
        this.memoizedGetConfiguration.set(memoKey, undefined);
      }
      return;
    }

    targetConfiguration =
      (targetConfigurations.length === 1 && targetConfigurations[0]) ||
      targetConfigurations.find((x: IAppConfiguration) =>
        this._matchAny([this._country], this._splitString(x.country))
      ) ||
      targetConfigurations.find((x: IAppConfiguration) =>
        this._roleMatch(
          this._userRoles.map((y: IRole) => y.roleCode),
          [x.role || ""]
        )
      ) ||
      // tslint:disable-next-line:max-line-length
      targetConfigurations.find((x: IAppConfiguration) =>
        this._roleMatch(
          this._userRoles.map((y: IRole) => y.roleCode),
          this._splitString(x.role_match)
        )
      ) ||
      // tslint:disable-next-line: max-line-length
      targetConfigurations.find((x: IAppConfiguration) =>
        this._roleAny(
          this._userRoles.map((y: IRole) => y.roleCode),
          this._splitString(x.role_any)
        )
      );

    if (!targetConfiguration) {
      console.warn(
        `Multiple features found without unique values.
                          Duplicated features must have unique %cCountry%c, %cRole%c, %cRole_Match%c or %cRole_Any%c properties.`,
        "color: blue",
        "",
        "color:blue",
        "",
        "color:blue",
        "",
        "color: blue",
        "",
        targetConfigurations
      );
      if (!isMemoized) {
        this.memoizedGetConfiguration.set(memoKey, undefined);
      }
      return;
    }

    if (!isMemoized) {
      this.memoizedGetConfiguration.set(memoKey, targetConfiguration);
    }
    return targetConfiguration;
  }

  private _getConfigurationName(configuration: string): string {
    const configurationSplitted: string[] = configuration.split("@");

    return configurationSplitted[0];
  }

  private _getConfigurationVersion(feature: string): string | undefined {
    const configurationSplitted: string[] = feature.split("@");

    return configurationSplitted[1] || undefined;
  }

  private _getProductReleaseVersion(feature: string): string | undefined {
    const configurationSplitted: string[] = feature.split("#");

    return configurationSplitted[1] || undefined;
  }

  private _splitString(value: string | string[] | undefined): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    return value.replace(/\s/g, "").split(",");
  }

  /**
   * Exact match
   * @param userRoles
   * @param neededRoles
   */
  private _roleMatch(userRoles: string[], neededRoles: string[]): boolean {
    const caseInsensitiveNeededRoles: string[] = neededRoles.map((x: string) => x.toUpperCase());
    const caseInsensitiveUserRoles: string[] = userRoles.map((x: string) => x.toUpperCase());

    if (
      caseInsensitiveNeededRoles.every(
        ($neededRole: string) =>
          $neededRole === caseInsensitiveUserRoles.find(($userRole: string) => $userRole === $neededRole)
      )
    ) {
      return true;
    }
    return false;
  }

  /**
   * Match at least one role
   * @param userRoles
   * @param neededRoles
   */
  private _roleAny(userRoles: string[], neededRoles: string[]): boolean {
    const caseInsensitiveNeededRoles: string[] = neededRoles.map((x: string) => x.toUpperCase());
    const caseInsensitiveUserRoles: string[] = userRoles.map((x: string) => x.toUpperCase());

    if (
      caseInsensitiveNeededRoles.some(
        ($neededRole: string) =>
          $neededRole === caseInsensitiveUserRoles.find(($userRole: string) => $userRole === $neededRole)
      )
    ) {
      return true;
    }
    return false;
  }

  private _matchAny(whatIHave: string[], mustMatchAnyOfThese: string[]): boolean {
    if (mustMatchAnyOfThese.some((x: string) => x === whatIHave.find((y: string) => y === x))) {
      return true;
    }
    return false;
  }

  private _loadConfigurations(): void {
    const options = {
      headers: new HttpHeaders({
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "Sat, 01 Jan 2000 00:00:00 GMT",
      }),
    } as CmxHttpOptions;

    if (this.configuration.useLocalManifest && !this.configuration.localManifestUrl) {
      console.error("%cuseLocalManifestUrl%c must be provided.", "color:blue", "");
    }

    if (!this.configuration.useLocalManifest && !this.configuration.loadConfigurations) {
      console.error(
        "%cloadFeatures(): IFeature[]%c must be declared if %cuseLocalManifest%c is false",
        "color: blue",
        "",
        "color: blue",
        ""
      );
    }

    if (this.configuration.useLocalManifest && this.configuration.localManifestUrl) {
      this.http
        .get(this.configuration.productPath + this.configuration.localManifestUrl, options)
        .toPromise()
        .then(
          (request: any) => {
            AppConfigurationService._cachedConfigurations = request;
            this.configurationsLoaded.next(true);
          },
          (rejected: any) => {
            console.warn(rejected);
            this.configurationsLoaded.next(false);
          }
        );
    } else if (typeof this.configuration.loadConfigurations === "function") {
      AppConfigurationService._cachedConfigurations = this.configuration.loadConfigurations();

      // tslint:disable-next-line: triple-equals
      if (AppConfigurationService._cachedConfigurations != undefined) {
        this.configurationsLoaded.next(true);
      }

      this.configurationsLoaded.next(false);
    } else {
      // TODO: Request the API if there's no other option specified
      // this.http.get(
      //         this.configuration.remoteManifestUrl,
      //         { headers: noCacheHeaders } as RequestOptionsArgs,
      //     ).toPromise()
      //     .then((request: any) => {
      // tslint:disable-next-line:comment-format
      //         AppConfigurationService._cachedFeatures = request.json();
      //         this.configurationsLoaded.next(true);
      //     },    (rejected: any) => {
      //         console.warn(rejected);
      this.configurationsLoaded.next(false);
      //     });
    }
  }

  private _getCountry(): string | undefined {
    const country: string | null = this.window.sessionStorage.getItem("country");
    if (!country) {
      console.error("Country not found. There must be a %ccountry%c string defined in the storage.", "color:blue", "");
      return;
    }

    return country.trim();
  }

  private _getAllUserRoles(): IRole[] {
    const applications = this.storageManagerService.user_applications;
    if (applications.length === 0) {
      console.error(
        "User applications not found. There must be a %cuser_applications%c array defined in the storage.",
        "color: blue",
        ""
      );
      return [];
    }

    const roles = applications.reduce((a, b) => {
      return a.concat(b.roles);
    }, []);

    return roles;
  }

  private serializeMemoKey(...args: any[]): string {
    let key = "";
    for (const e of args) {
      key += JSON.stringify(e);
    }
    return key;
  }
}
